import { useEffect, useState } from 'react';
import {
  BuildingOfficeIcon,
  UsersIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { businessApi, bookingApi } from '../services/api';
import { Business, Booking } from '../types';

interface Stats {
  totalBusinesses: number;
  totalBookings: number;
  pendingBookings: number;
  totalServices: number;
}

export function Dashboard() {
  const [stats, setStats] = useState<Stats>({
    totalBusinesses: 0,
    totalBookings: 0,
    pendingBookings: 0,
    totalServices: 0,
  });
  const [recentBookings, setRecentBookings] = useState<Booking[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [businessesRes, bookingsRes] = await Promise.all([
        businessApi.getMyBusinesses(),
        bookingApi.getMyBookings({ limit: 5 }),
      ]);

      const businessesData = businessesRes.data.data.businesses || [];
      const bookingsData = bookingsRes.data.data.bookings || [];

      setBusinesses(businessesData);
      setRecentBookings(bookingsData);

      // Calculate stats
      const pendingCount = bookingsData.filter(
        (b: Booking) => b.status === 'PENDING'
      ).length;

      const totalServices = businessesData.reduce(
        (acc: number, b: Business) => acc + (b._count?.services || 0),
        0
      );

      setStats({
        totalBusinesses: businessesData.length,
        totalBookings: bookingsRes.data.data.pagination?.total || 0,
        pendingBookings: pendingCount,
        totalServices,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const statCards = [
    {
      name: 'Businesses',
      value: stats.totalBusinesses,
      icon: BuildingOfficeIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Total Bookings',
      value: stats.totalBookings,
      icon: CalendarIcon,
      color: 'bg-green-500',
    },
    {
      name: 'Pending Bookings',
      value: stats.pendingBookings,
      icon: ClipboardDocumentListIcon,
      color: 'bg-yellow-500',
    },
    {
      name: 'Services',
      value: stats.totalServices,
      icon: UsersIcon,
      color: 'bg-purple-500',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div key={stat.name} className="card p-6">
            <div className="flex items-center">
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-500">{stat.name}</p>
                <p className="text-2xl font-bold text-secondary-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Bookings */}
      <div className="card">
        <div className="px-6 py-4 border-b border-secondary-200">
          <h3 className="text-lg font-semibold text-secondary-900">Recent Bookings</h3>
        </div>
        <div className="overflow-x-auto">
          {recentBookings.length > 0 ? (
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Business
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {recentBookings.map((booking) => (
                  <tr key={booking.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">
                      {booking.service?.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                      {booking.business?.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                      {new Date(booking.date).toLocaleDateString()} at {booking.time}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          booking.status === 'CONFIRMED'
                            ? 'bg-green-100 text-green-800'
                            : booking.status === 'PENDING'
                            ? 'bg-yellow-100 text-yellow-800'
                            : booking.status === 'CANCELLED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {booking.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-6 py-8 text-center text-secondary-500">
              No bookings yet. Start by creating a business and services!
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <a href="/businesses" className="btn-primary">
            Manage Businesses
          </a>
          <a href="/services" className="btn-secondary">
            View Services
          </a>
          <a href="/bookings" className="btn-secondary">
            View All Bookings
          </a>
        </div>
      </div>
    </div>
  );
}
